import { ConditionState } from "@builder/hooks/useConditions";
import { DynamicDataContext } from "@common/build/activeEntityType";
import { GlobalBlockTypes } from "@common/build/types";
import { PayloadAction, createSlice } from "@reduxjs/toolkit";

export type PopupKey =
    | "icons"
    | "templateLibrary"
    | "fullDesignSets"
    | "globalBlocks"
    | "conditions"
    | "dynamicData"
    | "publishBuild"
    | "actionBuilder"
    | "images"
    | "actions"
    | "logout"
    | "onboardComplete";

interface DynamicDataField {
    dynamicDataType: DynamicDataContext | null;
    actionDynamicData: string | null;
}
interface DynamicDataField {
    dynamicDataType: DynamicDataContext | null;
    actionDynamicData: string | null;
}

type InitialState = {
    popups: PopupKey[];
    defaultValues: DefaultValuesCondition | null;
    isAutomation: boolean;
    templateLibraryFilter: string | null;
    iconState: (string | number)[] | null;
    imageState: string[] | null;
    globalBlocks: GlobalBlockTypes | null;
    actionFromNode: boolean;
    currentId: string | null;
    dynamicDataFields: Record<string, DynamicDataField>;
    actionPath: string | null;
};

export interface DefaultValuesCondition {
    id: string;
    name: string;
    actionId?: string;
    conditionState: ConditionState;
}

const initialState: InitialState = {
    popups: [],
    defaultValues: null,
    isAutomation: false,
    templateLibraryFilter: null,
    iconState: null,
    imageState: null,
    globalBlocks: null,
    actionFromNode: false,
    actionPath: null,
    currentId: null,
    dynamicDataFields: {}
};

const popupSlice = createSlice({
    name: "popup",
    initialState: initialState,
    reducers: {
        setCurrentPopup: (state, action: PayloadAction<PopupKey>) => {
            if (!state.popups.includes(action.payload)) {
                if (state.popups.length > 0) {
                    state.popups = [...state.popups, action.payload];
                } else {
                    state.popups.push(action.payload);
                }
            }
            if (!state.popups.includes(action.payload)) {
                if (state.popups.length > 0) {
                    state.popups = [...state.popups, action.payload];
                } else {
                    state.popups.push(action.payload);
                }
            }
        },
        closePopup: state => {
            if (state.popups.length > 0) {
                state.popups.pop();
            }
            if (state.popups.length === 0) {
                state.defaultValues = null;
                state.isAutomation = false;
                state.actionFromNode = false;
                state.actionPath = null;
                state.currentId = null;
                state.dynamicDataFields = {};
            }
        },
        setDefaultValues: (state, action: PayloadAction<DefaultValuesCondition>) => {
            state.defaultValues = action.payload;
        },
        setIsAutomation: (state, action: PayloadAction<boolean>) => {
            state.isAutomation = action.payload;
        },
        setTemplateLibraryFilter: (state, action: PayloadAction<string>) => {
            state.templateLibraryFilter = action.payload;
        },
        setIconState: (state, action: PayloadAction<(string | number)[] | null>) => {
            state.iconState = action.payload;
        },
        setImageState: (state, action: PayloadAction<string[] | null>) => {
            state.imageState = action.payload;
        },
        setGlobalBlockPopupState: (state, action: PayloadAction<GlobalBlockTypes | null>) => {
            state.globalBlocks = action.payload;
        },
        setActionFromNode: (state, action: PayloadAction<boolean>) => {
            state.actionFromNode = action.payload;
        },
        setDynamicDataType: (state, action: PayloadAction<{ id: string; type: DynamicDataContext | null }>) => {
            const { id, type } = action.payload;
            if (!state.dynamicDataFields[id]) {
                state.dynamicDataFields[id] = { dynamicDataType: null, actionDynamicData: null };
            }
            state.dynamicDataFields[id].dynamicDataType = type;
        },
        setActionDynamicData: (state, action: PayloadAction<{ id: string; data: string | null }>) => {
            const { id, data } = action.payload;
            if (!state.dynamicDataFields[id]) {
                state.dynamicDataFields[id] = { dynamicDataType: null, actionDynamicData: null };
            }
            state.dynamicDataFields[id].actionDynamicData = data;
        },
        removeDynamicDataField: (state, action: PayloadAction<string>) => {
            const id = action.payload;
            if (state.dynamicDataFields[id]) {
                delete state.dynamicDataFields[id];
            }
        },
        setCurrentId: (state, action: PayloadAction<string | null>) => {
            state.currentId = action.payload;
        },
        resetDynamicDataFields: state => {
            state.dynamicDataFields = {};
        },
        setActionPath: (state, action: PayloadAction<string | null>) => {
            state.actionPath = action.payload;
        }
    }
});

export default popupSlice.reducer;
export const {
    setCurrentPopup,
    closePopup,
    setDefaultValues,
    setIsAutomation,
    setTemplateLibraryFilter,
    setIconState,
    setImageState,
    setGlobalBlockPopupState,
    setActionFromNode,
    setActionPath,
    setDynamicDataType,
    setActionDynamicData,
    setCurrentId,
    resetDynamicDataFields,
    removeDynamicDataField
} = popupSlice.actions;
